import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, User, BookOpen, Sparkles, ExternalLink, RefreshCw, X, ChevronRight, ChevronUp, ChevronDown, Pin, Layers } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { blogConfig } from '../data/mock';
import blogsArchive from '../data/blogsArchive.json';
import { getSlugFromArticle } from '../utils/blogUtils';

// Clean HTML tags for card excerpt previews
function cleanExcerpt(htmlStr) {
  if (!htmlStr) return '';
  const doc = new DOMParser().parseFromString(htmlStr, 'text/html');
  const text = doc.body.textContent || "";
  return text.length > 160 ? text.substring(0, 160).trim() + '...' : text;
}

// Native XML parser for Medium RSS feeds
function parseMediumRssXml(xmlString, feedInfo = {}) {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const items = xmlDoc.querySelectorAll('item');
    
    return Array.from(items).map((item) => {
      const title = item.querySelector('title')?.textContent || '';
      let link = item.querySelector('link')?.textContent || '';
      if (link.includes('?')) link = link.split('?')[0];
      
      const pubDate = item.querySelector('pubDate')?.textContent || new Date().toISOString();
      const creator = item.getElementsByTagNameNS('*', 'creator')[0]?.textContent || feedInfo.name || 'Team India FGC';
      const content = item.getElementsByTagNameNS('*', 'encoded')[0]?.textContent || item.querySelector('description')?.textContent || '';
      
      const categories = Array.from(item.querySelectorAll('category')).map(c => c.textContent.replace(/-/g, ' ').toUpperCase());
      
      const imageMatch = content.match(/<img[^>]+src=["']([^"']+)["']/i);
      const thumbnail = imageMatch ? imageMatch[1] : '/assets/images/2026.jpg';

      return {
        title,
        pubDate: new Date(pubDate).toISOString(),
        link,
        author: creator,
        sourceName: feedInfo.name || 'Medium',
        badgeColor: feedInfo.badgeColor || 'bg-midnight text-white',
        thumbnail,
        categories: categories.length > 0 ? categories : ['ROBOTICS', 'STEM'],
        excerpt: cleanExcerpt(content),
        fullContent: content
      };
    });
  } catch (e) {
    console.error('Error parsing RSS XML:', e);
    return [];
  }
}

// Enhance & sanitize Medium/RSS HTML for Medium-like typography in Modal
function formatMediumArticleHtml(htmlContent) {
  if (!htmlContent) return '';
  
  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
  
  // Remove tracking pixels, stats, or empty figcaptions
  doc.querySelectorAll('img[src*="stat?event"], figcaption:empty').forEach(el => el.remove());

  // 1. Process Embeds & YouTube / Medium Media links
  doc.querySelectorAll('iframe').forEach(iframe => {
    let src = iframe.getAttribute('src') || '';
    
    // Extract nested YouTube URL from Embedly widget iframe URL if present
    if (src.includes('embedly.com') || src.includes('youtube.com') || src.includes('youtu.be')) {
      let ytMatch = src.match(/youtube\.com%2Fembed%2F([a-zA-Z0-9_-]+)/i) || 
                   src.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i) || 
                   src.match(/v%3D([a-zA-Z0-9_-]+)/i) || 
                   src.match(/v=([a-zA-Z0-9_-]+)/i);
      if (ytMatch) {
        src = `https://www.youtube.com/embed/${ytMatch[1]}`;
      }
    }
    
    iframe.setAttribute('src', src);
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.className = 'w-full aspect-video rounded-2xl my-10 border border-pragmatic/20 shadow-lg bg-midnight';
  });

  // Handle standalone medium.com/media/ href links or YouTube links
  doc.querySelectorAll('a').forEach(a => {
    const href = a.getAttribute('href') || '';
    const text = a.textContent.trim();

    // Check if it's a YouTube link
    let ytId = href.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/i) || href.match(/youtu\.be\/([a-zA-Z0-9_-]+)/i) || text.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/i);
    if (ytId) {
      const wrapper = doc.createElement('div');
      wrapper.className = 'my-10 w-full';
      wrapper.innerHTML = `<iframe src="https://www.youtube.com/embed/${ytId[1]}" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="w-full aspect-video rounded-2xl border border-pragmatic/20 shadow-lg bg-midnight"></iframe>`;
      if (a.parentNode && a.parentNode.tagName === 'P' && a.parentNode.childNodes.length === 1) {
        a.parentNode.replaceChild(wrapper, a);
      } else {
        a.replaceWith(wrapper);
      }
      return;
    }

    // Check if it's a raw medium.com/media/ link
    if (href.includes('medium.com/media/') || text.includes('medium.com/media/')) {
      if (a.parentNode && a.parentNode.tagName === 'P' && a.parentNode.childNodes.length === 1) {
        a.className = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-dwarfpony/10 text-dwarfpony font-bold text-sm hover:bg-dwarfpony/20 transition-colors my-4 border border-dwarfpony/20';
        a.innerHTML = `<span>View Interactive Media Embed</span> <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;
        return;
      }
    }

    a.className = 'text-dwarfpony hover:text-dwarfpony-dark font-semibold underline underline-offset-4 decoration-dwarfpony/40 hover:decoration-dwarfpony transition-colors';
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });

  // 2. Format Figures & Captions
  doc.querySelectorAll('figure').forEach(fig => {
    fig.className = 'my-10 text-center w-full overflow-hidden';
  });

  doc.querySelectorAll('figcaption').forEach(fc => {
    fc.className = 'text-center text-xs sm:text-sm font-sans text-heavyrain italic mt-3 mb-2 px-4 block max-w-xl mx-auto';
  });

  // 3. Format Images (Uncropped, crisp, centered)
  doc.querySelectorAll('img').forEach(img => {
    img.className = 'w-full h-auto rounded-2xl shadow-md my-4 border border-pragmatic/20 object-contain mx-auto block max-w-full';
  });

  // 4. Format Blockquotes
  doc.querySelectorAll('blockquote').forEach(bq => {
    bq.className = 'border-l-4 border-dwarfpony pl-6 italic text-midnight text-xl sm:text-2xl bg-dwarfpony/5 p-6 rounded-r-2xl my-10 font-serif leading-relaxed shadow-xs';
  });

  // 5. Format Subtitles vs Headings
  doc.querySelectorAll('h1, h2, h3, h4').forEach(h => {
    const text = h.textContent.trim();
    if (text.toLowerCase().startsWith('by ') || text.toLowerCase().includes('by stem education trust') || text.toLowerCase().includes('official partner')) {
      h.className = 'font-serif text-heavyrain italic text-lg sm:text-xl font-normal my-4 leading-relaxed border-none block';
    } else {
      h.className = 'font-sans font-extrabold text-midnight mt-12 mb-6 tracking-tight leading-snug text-2xl sm:text-3xl border-b border-pragmatic/15 pb-3';
    }
  });

  // 6. Format Paragraphs & Lead Paragraph
  const paragraphs = doc.querySelectorAll('p');
  paragraphs.forEach((p, idx) => {
    const text = p.textContent.trim();

    if (text.toLowerCase().startsWith('by ') && idx < 3) {
      p.className = 'font-serif text-heavyrain italic text-lg sm:text-xl font-normal my-4 leading-relaxed block';
    } else if (idx === 0) {
      p.className = 'font-serif text-midnight text-xl sm:text-2xl leading-relaxed mb-8 font-medium';
    } else {
      p.className = 'font-serif text-deepwalnut text-lg sm:text-xl leading-relaxed mb-8 tracking-normal';
    }
  });

  // 7. Format Lists
  doc.querySelectorAll('ul').forEach(ul => {
    ul.className = 'list-disc list-outside space-y-3 mb-8 font-serif text-deepwalnut text-lg sm:text-xl pl-6 leading-relaxed';
  });
  doc.querySelectorAll('ol').forEach(ol => {
    ol.className = 'list-decimal list-outside space-y-3 mb-8 font-serif text-deepwalnut text-lg sm:text-xl pl-6 leading-relaxed';
  });

  return doc.body.innerHTML;
}

// Extract first image from HTML content if thumbnail missing
function extractImageFromContent(htmlContent, fallbackImg) {
  if (!htmlContent) return fallbackImg;
  const match = htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : fallbackImg;
}

const BlogsPage = () => {
  const navigate = useNavigate();
  // Initialize with static archived blogs for instant 0ms load speed
  const [blogs, setBlogs] = useState(blogsArchive || []);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSource, setSelectedSource] = useState('All');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedSource]);

  // Fetch all configured RSS feeds concurrently & merge chronologically
  const fetchAllRssFeeds = async () => {
    setIsRefreshing(true);
    try {
      const feedsToFetch = blogConfig.rssFeeds || [
        { name: "Medium", url: "https://medium.com/feed/@teamindiafgc", defaultCategory: "ROBOTICS" }
      ];

      const feedPromises = feedsToFetch.map(async (feed) => {
        try {
          // 1. First try native Netlify Edge proxy /api/medium-rss (Zero third-party dependency, handles millions of visitors)
          try {
            const proxyRes = await fetch('/api/medium-rss');
            if (proxyRes.ok) {
              const xmlText = await proxyRes.text();
              const parsedItems = parseMediumRssXml(xmlText, feed);
              if (parsedItems && parsedItems.length > 0) {
                return parsedItems;
              }
            }
          } catch (proxyErr) {
            // Proxy unavailable (e.g. running on localhost without Netlify dev)
          }

          // 2. Fallback to rss2json proxy for local development
          const res = await fetch(
            `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feed.url)}`
          );
          const data = await res.json();
          if (data && data.status === 'ok' && data.items) {
            return data.items.map((item) => {
              const rawContent = item.content || item.description || '';
              const image = item.thumbnail || extractImageFromContent(rawContent, '/assets/images/2026.jpg');
              
              const rawCategories = item.categories || [];
              const categories = rawCategories.length > 0 
                ? rawCategories.map(c => c.replace(/-/g, ' ').toUpperCase()) 
                : [feed.defaultCategory || 'STEM'];

              return {
                title: item.title,
                pubDate: item.pubDate,
                link: item.link,
                author: item.author || feed.name,
                sourceName: feed.name,
                badgeColor: feed.badgeColor || 'bg-midnight text-white',
                thumbnail: image,
                categories: categories,
                excerpt: cleanExcerpt(rawContent),
                fullContent: formatMediumArticleHtml(rawContent)
              };
            });
          }
          return [];
        } catch (err) {
          console.error(`Error fetching feed ${feed.name}:`, err);
          return [];
        }
      });

      const results = await Promise.all(feedPromises);
      const livePosts = results.flat();

      // Deduplicate and merge archived posts with live RSS feed posts
      const postsMap = new Map();

      // Seed with archive posts
      (blogsArchive || []).forEach((post) => {
        const key = (post.link || post.title).split('?')[0];
        postsMap.set(key, post);
      });

      // Overlay live RSS feed posts
      livePosts.forEach((post) => {
        const key = (post.link || post.title).split('?')[0];
        postsMap.set(key, post);
      });

      const combinedPosts = Array.from(postsMap.values());

      // Sort ALL combined posts in descending chronological order (newest first)
      combinedPosts.sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));

      setBlogs(combinedPosts);
    } catch (err) {
      console.error('Error loading RSS feeds, using archive fallback:', err);
      if (blogsArchive && blogsArchive.length > 0) {
        setBlogs(blogsArchive);
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllRssFeeds();
  }, []);

  const [showAllTags, setShowAllTags] = useState(false);

  // Dynamically extract all unique categories from RSS articles
  const availableCategories = ['All', ...Array.from(new Set(blogs.flatMap((b) => b.categories || [])))];
  
  // Show top categories dynamically fitting single line + "+ X More" button
  const visibleCategories = showAllTags ? availableCategories : availableCategories.slice(0, 5);
  const hiddenCount = Math.max(0, availableCategories.length - 5);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.author.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === 'All' || (blog.categories && blog.categories.some(c => c.toLowerCase() === selectedCategory.toLowerCase()));

    const matchesSource =
      selectedSource === 'All' || blog.sourceName === selectedSource;

    return matchesSearch && matchesCategory && matchesSource;
  });

  // Determine Pinned Featured Post from mock.js config (pinnedPostTitle)
  let featuredPost = null;
  let gridPosts = [...filteredBlogs];

  if (filteredBlogs.length > 0) {
    const pinnedTitle = blogConfig.pinnedPostTitle;
    if (pinnedTitle) {
      const foundIdx = filteredBlogs.findIndex(b => b.title.toLowerCase().includes(pinnedTitle.toLowerCase()));
      if (foundIdx !== -1) {
        featuredPost = filteredBlogs[foundIdx];
        gridPosts = filteredBlogs.filter((_, idx) => idx !== foundIdx);
      } else {
        featuredPost = filteredBlogs[0];
        gridPosts = filteredBlogs.slice(1);
      }
    } else {
      featuredPost = filteredBlogs[0];
      gridPosts = filteredBlogs.slice(1);
    }
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="blogs-page">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-br from-cream via-pragmatic/10 to-richolive/5 border-b border-pragmatic/20">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pragmatic/15 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-2 text-dwarfpony text-xs font-extrabold tracking-widest uppercase mb-4 px-4 py-1.5 bg-dwarfpony/10 rounded-full border border-dwarfpony/20">
                <Sparkles size={14} className="text-dwarfpony" />
                STEM & Innovation Journal
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-midnight mb-6 leading-tight tracking-tight">
                Stories of <span className="text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony to-dwarfpony-dark">Impact & Innovation</span>
              </h1>
              <p className="text-lg md:text-xl text-deepwalnut leading-relaxed max-w-2xl mx-auto mb-8">
                Explore competition highlights, student stories, technical insights, and community initiatives from Team India and STEM Education Trust.
              </p>

              <div className="flex items-center justify-center">
                <a
                  href="https://medium.com/@teamindiafgc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-midnight text-white text-xs font-bold rounded-xl hover:bg-midnight/90 transition-all shadow-md"
                >
                  <BookOpen size={15} />
                  Follow @teamindiafgc on Medium
                  <ExternalLink size={13} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Filter & Search Bar */}
        <section className="py-4 bg-cream-dark border-b border-pragmatic/20 sticky top-20 z-30 shadow-sm backdrop-blur-md bg-cream-dark/95">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Category Pills (Dynamic Single Line with + More toggle) */}
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                {visibleCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3.5 py-1.5 text-[11px] font-bold rounded-full transition-all whitespace-nowrap border ${
                      selectedCategory === cat
                        ? 'bg-dwarfpony text-white border-dwarfpony shadow-md shadow-dwarfpony/25'
                        : 'bg-cream text-heavyrain border-pragmatic/30 hover:border-dwarfpony/40 hover:text-deepwalnut'
                    }`}
                  >
                    {cat}
                  </button>
                ))}

                {hiddenCount > 0 && !showAllTags && (
                  <button
                    onClick={() => setShowAllTags(true)}
                    className="px-3.5 py-1.5 text-[11px] font-black text-dwarfpony bg-dwarfpony/10 border border-dwarfpony/30 rounded-full hover:bg-dwarfpony hover:text-white transition-all whitespace-nowrap flex items-center gap-1 shadow-xs"
                  >
                    <span>+ {hiddenCount} More</span>
                    <ChevronDown size={13} />
                  </button>
                )}

                {showAllTags && availableCategories.length > 5 && (
                  <button
                    onClick={() => setShowAllTags(false)}
                    className="px-3.5 py-1.5 text-[11px] font-black text-midnight bg-midnight/10 border border-midnight/30 rounded-full hover:bg-midnight hover:text-white transition-all whitespace-nowrap flex items-center gap-1 shadow-xs"
                  >
                    <span>Show Less</span>
                    <ChevronUp size={13} />
                  </button>
                )}
              </div>

              {/* Search Bar */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-heavyrain" size={16} />
                <Input
                  type="text"
                  placeholder="Search stories, topics, or authors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 text-xs bg-cream border-pragmatic/30 rounded-xl focus:border-dwarfpony focus:ring-1 focus:ring-dwarfpony text-deepwalnut"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Grid */}
        <section className="py-12 md:py-16 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              /* Loading Skeletons */
              <div className="space-y-8">
                <div className="w-full h-80 bg-cream-dark border border-pragmatic/20 rounded-3xl animate-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="h-96 bg-cream-dark border border-pragmatic/20 rounded-3xl animate-pulse" />
                  ))}
                </div>
              </div>
            ) : filteredBlogs.length === 0 ? (
              /* Empty Search State */
              <div className="text-center py-20 bg-cream-dark rounded-3xl border border-pragmatic/30 max-w-xl mx-auto">
                <BookOpen size={48} className="mx-auto text-heavyrain/40 mb-4" />
                <h3 className="text-xl font-bold text-deepwalnut mb-2">No stories found</h3>
                <p className="text-heavyrain text-xs mb-6">
                  Try adjusting your search query or clear your active category filters.
                </p>
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('All');
                    setSelectedSource('All');
                  }}
                  className="bg-dwarfpony hover:bg-dwarfpony-dark text-white rounded-xl text-xs font-bold"
                >
                  Reset Filters
                </Button>
              </div>
            ) : (
              <>
                {/* Featured Pinned Post (Entire Banner Clickable) */}
                {featuredPost && selectedCategory === 'All' && !searchQuery && (
                  <div className="mb-14">
                    <div 
                      onClick={() => navigate(`/blogs/${getSlugFromArticle(featuredPost)}`)}
                      className="bg-cream-dark border border-pragmatic/30 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 grid grid-cols-1 lg:grid-cols-12 gap-0 group cursor-pointer"
                    >
                      {/* Banner Cover Image */}
                      <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden bg-midnight">
                        <img
                          src={featuredPost.thumbnail}
                          alt={featuredPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute top-4 left-4 flex gap-2">
                          <Badge className="bg-dwarfpony text-white text-xs font-extrabold uppercase px-3 py-1 rounded-full border-none shadow-md flex items-center gap-1">
                            <Pin size={12} />
                            {blogConfig.pinnedPostTitle ? 'Pinned Story' : 'Featured Story'}
                          </Badge>
                          {featuredPost.categories.slice(0, 1).map((cat, idx) => (
                            <Badge key={idx} className="bg-midnight/80 backdrop-blur-md text-white text-xs font-bold uppercase px-3 py-1 rounded-full border-none">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Banner Article Content */}
                      <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-4 text-xs font-semibold text-heavyrain mb-4">
                            <span className="flex items-center gap-1.5">
                              <Calendar size={14} className="text-dwarfpony" />
                              {new Date(featuredPost.pubDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <User size={14} className="text-richolive" />
                              {featuredPost.author}
                            </span>
                          </div>

                          <h2 className="text-2xl lg:text-3xl font-extrabold text-midnight mb-4 leading-tight group-hover:text-dwarfpony transition-colors">
                            {featuredPost.title}
                          </h2>

                          <p className="text-heavyrain text-sm leading-relaxed mb-6">
                            {featuredPost.excerpt}
                          </p>
                        </div>

                        <div className="flex items-center gap-3 pt-6 border-t border-pragmatic/20">
                          <Button
                            className="bg-dwarfpony hover:bg-dwarfpony-dark text-white font-bold rounded-xl text-xs px-5 py-2.5 shadow-md flex items-center gap-2"
                          >
                            <span>Read Full Article</span>
                            <ChevronRight size={16} />
                          </Button>
                          <a
                            href={featuredPost.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2.5 bg-cream border border-pragmatic/30 text-heavyrain hover:text-midnight hover:border-dwarfpony/40 rounded-xl transition-all"
                            title={`Open on ${featuredPost.sourceName}`}
                          >
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Grid Cards for remaining posts (Paginated) */}
                {(() => {
                  const activeList = selectedCategory === 'All' && !searchQuery ? gridPosts : filteredBlogs;
                  const totalPages = Math.ceil(activeList.length / postsPerPage);
                  const startIndex = (currentPage - 1) * postsPerPage;
                  const currentPaginatedPosts = activeList.slice(startIndex, startIndex + postsPerPage);

                  return (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {currentPaginatedPosts.map((blog, idx) => (
                          <article
                            key={idx}
                            onClick={() => navigate(`/blogs/${getSlugFromArticle(blog)}`)}
                            className="bg-cream-dark border border-pragmatic/30 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:border-dwarfpony/40 transition-all duration-300 flex flex-col justify-between group cursor-pointer"
                          >
                            <div>
                              {/* Image Thumbnail */}
                              <div className="relative aspect-[16/10] overflow-hidden bg-midnight">
                                <img
                                  src={blog.thumbnail}
                                  alt={blog.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  onError={(e) => {
                                    e.target.src = '/assets/images/2026.jpg';
                                  }}
                                />
                                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                                  {blog.categories.slice(0, 2).map((cat, cIdx) => (
                                    <Badge key={cIdx} className="bg-midnight/80 backdrop-blur-md text-white text-[10px] font-bold border-none px-2.5 py-0.5 rounded-full uppercase">
                                      {cat}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              {/* Text Details */}
                              <div className="p-6">
                                <div className="flex items-center gap-3 text-xs font-semibold text-heavyrain mb-3">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={13} className="text-dwarfpony" />
                                    {new Date(blog.pubDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <User size={13} className="text-richolive" />
                                    {blog.author}
                                  </span>
                                </div>

                                <h3 className="text-lg font-bold text-deepwalnut mb-3 leading-snug group-hover:text-dwarfpony transition-colors">
                                  {blog.title}
                                </h3>

                                <p className="text-heavyrain text-xs leading-relaxed line-clamp-3 mb-4">
                                  {blog.excerpt}
                                </p>
                              </div>
                            </div>

                            {/* Card Action Bar */}
                            <div className="px-6 pb-6 pt-0 flex items-center justify-between border-t border-pragmatic/15 pt-4">
                              <span
                                className="text-dwarfpony group-hover:text-dwarfpony-dark text-xs font-extrabold flex items-center gap-1 transition-colors"
                              >
                                <span>Read Full Story</span>
                                <ChevronRight size={14} />
                              </span>
                              <a
                                href={blog.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-heavyrain hover:text-midnight text-xs font-semibold flex items-center gap-1 transition-colors"
                                title={`Open on ${blog.sourceName}`}
                              >
                                <span>{blog.sourceName}</span>
                                <ExternalLink size={12} />
                              </a>
                            </div>
                          </article>
                        ))}
                      </div>

                      {/* Pagination Controls Bar */}
                      {totalPages > 1 && (
                        <div className="mt-12 pt-8 border-t border-pragmatic/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                          <span className="text-xs font-semibold text-heavyrain">
                            Showing <strong className="text-midnight">{startIndex + 1}</strong>–<strong className="text-midnight">{Math.min(startIndex + postsPerPage, activeList.length)}</strong> of <strong className="text-midnight">{activeList.length}</strong> stories
                          </span>

                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => {
                                setCurrentPage(p => Math.max(p - 1, 1));
                                window.scrollTo({ top: 400, behavior: 'smooth' });
                              }}
                              disabled={currentPage === 1}
                              variant="outline"
                              className="border-pragmatic/30 text-deepwalnut hover:bg-cream-dark text-xs font-bold rounded-xl px-4 py-2 disabled:opacity-40"
                            >
                              Previous
                            </Button>

                            <div className="flex items-center gap-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                                <button
                                  key={pg}
                                  onClick={() => {
                                    setCurrentPage(pg);
                                    window.scrollTo({ top: 400, behavior: 'smooth' });
                                  }}
                                  className={`w-8 h-8 text-xs font-bold rounded-lg transition-all ${
                                    currentPage === pg
                                      ? 'bg-dwarfpony text-white shadow-sm'
                                      : 'bg-cream border border-pragmatic/20 text-heavyrain hover:text-midnight'
                                  }`}
                                >
                                  {pg}
                                </button>
                              ))}
                            </div>

                            <Button
                              onClick={() => {
                                setCurrentPage(p => Math.min(p + 1, totalPages));
                                window.scrollTo({ top: 400, behavior: 'smooth' });
                              }}
                              disabled={currentPage === totalPages}
                              variant="outline"
                              className="border-pragmatic/30 text-deepwalnut hover:bg-cream-dark text-xs font-bold rounded-xl px-4 py-2 disabled:opacity-40"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogsPage;
